import React, { useState, useRef, useEffect } from 'react';
import { X, Eye, EyeOff, Globe, Mail, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';

// v60.1 — tooltip compte Pro (13 langues)
const PRO_HINT = {
  fr: "Compte Pro : donne accès à Destine Ads — publicités dans le feed et les stories, e-boutique, évènements MyMouv, offres MyJob, statistiques et AP-IA.",
  en: "Pro account: gives access to Destine Ads — ads in the feed and stories, e-shop, MyMouv events, MyJob offers, analytics and AP-IA.",
  es: "Cuenta Pro: da acceso a Destine Ads — anuncios en el feed y las historias, e-tienda, eventos MyMouv, ofertas MyJob, estadísticas y AP-IA.",
  ar: "الحساب الاحترافي: يمنح الوصول إلى Destine Ads — إعلانات في الخلاصة والقصص، متجر إلكتروني، فعاليات MyMouv، عروض MyJob، إحصاءات وAP-IA.",
  pt: "Conta Pro: dá acesso ao Destine Ads — anúncios no feed e nas stories, e-loja, eventos MyMouv, ofertas MyJob, estatísticas e AP-IA.",
  de: "Pro-Konto: Zugang zu Destine Ads — Anzeigen im Feed und in Stories, E-Shop, MyMouv-Events, MyJob-Angebote, Statistiken und AP-IA.",
  ru: "Pro-аккаунт: доступ к Destine Ads — реклама в ленте и историях, онлайн-магазин, события MyMouv, вакансии MyJob, статистика и AP-IA.",
  zh: "专业账户：可使用 Destine Ads——信息流与动态广告、电商店铺、MyMouv 活动、MyJob 招聘、数据统计与 AP-IA。",
  ja: "プロアカウント：Destine Ads を利用可能 — フィード/ストーリー広告、e ショップ、MyMouv イベント、MyJob 求人、統計、AP-IA。",
  hi: "प्रो खाता: Destine Ads तक पहुँच — फ़ीड और स्टोरीज़ में विज्ञापन, ई-शॉप, MyMouv इवेंट, MyJob ऑफ़र, आँकड़े और AP-IA।",
  nl: "Pro-account: geeft toegang tot Destine Ads — advertenties in de feed en stories, e-shop, MyMouv-evenementen, MyJob-vacatures, statistieken en AP-IA.",
  sw: "Akaunti ya Pro: inatoa ufikiaji wa Destine Ads — matangazo kwenye mtiririko na hadithi, duka la mtandaoni, matukio ya MyMouv, nafasi za MyJob, takwimu na AP-IA.",
  ht: "Kont Pro: bay aksè a Destine Ads — piblisite nan fil la ak story yo, e-boutik, evènman MyMouv, òf MyJob, estatistik ak AP-IA.",
};

const AuthModal = ({ onClose, initialMode = 'login' }) => {
  const { language, setLanguage, supportedLanguages, getLanguageName, getFlag, t } = useLanguage();
  const [mode, setMode] = useState(initialMode);
  const [accountType, setAccountType] = useState('ID');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    siret: '',
    date_of_birth: '',
    accepts_terms: false
  });
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [emailStatus, setEmailStatus] = useState(null); // null | 'checking' | 'valid' | 'invalid'
  const [emailError, setEmailError] = useState('');
  const emailCheckTimer = useRef(null);
  
  const modalContentRef = useRef(null);
  const { login, loginVerify, adoptSession } = useAuth();
  const navigate = useNavigate();

  // OTP 2FA flow state — set when backend answers `two_factor: true`
  // (login) or when a signup succeeds. Shows the code input overlay.
  const [otpFlow, setOtpFlow] = useState(null); // { kind: 'login'|'signup', userId, emailMasked }
  const [otpCode, setOtpCode] = useState('');
  const [otpResending, setOtpResending] = useState(false);

  // Load captcha when switching to register or login mode
  useEffect(() => {
    if (mode === 'register' || mode === 'login') {
      loadCaptcha();
    }
  }, [mode]);

  const loadCaptcha = async () => {
    try {
      const res = await axios.get('/api/auth/captcha');
      setCaptcha(res.data);
      setCaptchaAnswer('');
    } catch (e) {
      console.error('Failed to load captcha');
    }
  };

  // Handle iOS keyboard visibility and scroll input into view
  useEffect(() => {
    const isIOS = Capacitor.getPlatform() === 'ios' || /iPhone|iPod/.test(navigator.userAgent);
    
    if (!isIOS) return;

    const handleFocus = (e) => {
      if (e.target.tagName === 'INPUT') {
        setKeyboardVisible(true);
        // Delay to let the keyboard appear
        setTimeout(() => {
          e.target.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        }, 300);
      }
    };

    const handleBlur = () => {
      setTimeout(() => {
        setKeyboardVisible(false);
      }, 100);
    };

    // Add listeners to all inputs in the modal
    const inputs = modalContentRef.current?.querySelectorAll('input');
    inputs?.forEach(input => {
      input.addEventListener('focus', handleFocus);
      input.addEventListener('blur', handleBlur);
    });

    return () => {
      inputs?.forEach(input => {
        input.removeEventListener('focus', handleFocus);
        input.removeEventListener('blur', handleBlur);
      });
    };
  }, [mode]); // Re-attach when mode changes (different inputs appear)

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Verify captcha for both login and register
      if (captcha && !captchaAnswer.trim()) {
        toast.error('Veuillez résoudre le calcul anti-robot');
        setLoading(false);
        return;
      }
      
      if (mode === 'login') {
        // Verify captcha locally before login
        if (captcha && captchaAnswer.trim()) {
          try {
            // We verify by attempting a dummy register with only captcha to check
            // Actually, let's just do client-side math verification
            const q = captcha.question;
            const parts = q.split(' ');
            const a = parseInt(parts[0]);
            const op = parts[1];
            const b = parseInt(parts[2]);
            let expected;
            if (op === '+') expected = a + b;
            else if (op === '-') expected = a - b;
            else if (op === 'x') expected = a * b;
            if (parseInt(captchaAnswer) !== expected) {
              toast.error('Vérification anti-robot incorrecte');
              loadCaptcha();
              setLoading(false);
              return;
            }
          } catch (e) {
            // If parsing fails, proceed anyway
          }
        }
        
        const loginRes = await login(formData.email, formData.password);
        if (loginRes?.two_factor === true) {
          // 2FA required: show OTP overlay
          setOtpFlow({ kind: 'login', userId: loginRes.user_id, emailMasked: loginRes.email_masked });
          setOtpCode('');
          setLoading(false);
          toast.success(`Code envoyé à ${loginRes.email_masked}`);
          return;
        }
        toast.success('Connexion réussie!');
        
        // Close modal first, then navigate
        // Use setTimeout to let React finish DOM updates before navigation
        setTimeout(() => {
          if (onClose) onClose();
          setTimeout(() => {
            navigate('/films', { replace: true });
          }, 100);
        }, 100);
        
      } else if (mode === 'register') {
        if (!formData.accepts_terms) {
          toast.error('Vous devez accepter les CGU');
          setLoading(false);
          return;
        }
        
        if (captcha && !captchaAnswer.trim()) {
          toast.error('Veuillez résoudre le calcul anti-robot');
          setLoading(false);
          return;
        }
        
        const regRes = await axios.post('/api/auth/register', {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          account_type: accountType,
          siret: formData.siret || null,
          date_of_birth: formData.date_of_birth || null,
          accepts_terms: formData.accepts_terms,
          captcha_answer: captchaAnswer.trim(),
          captcha_token: captcha?.token,
          captcha_question: captcha?.question,
          language: language || 'fr',
        });

        // Persist the fresh token so /auth/me/email/verify-code is authenticated.
        if (regRes.data?.token) {
          try {
            const { default: storage } = await import('../utils/storage');
            await storage.set('auth_token', regRes.data.token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${regRes.data.token}`;
          } catch (_e) { /* ignore */ }
        }

        // Show OTP overlay — user must enter the code received by email
        // before entering the app (24h validity).
        const email = formData.email;
        const parts = email.split('@');
        const masked = parts[0].length > 2 ? (parts[0][0] + '***' + parts[0].slice(-1)) : '***';
        setOtpFlow({ kind: 'signup', userId: regRes.data.user?.id, emailMasked: `${masked}@${parts[1] || ''}`, token: regRes.data?.token, user: regRes.data?.user });
        setOtpCode('');
        setLoading(false);
        toast.success(`Code envoyé à ${email}. Consultez votre boîte mail.`);
        return;
        
      } else if (mode === 'forgot') {
        if (!formData.date_of_birth) {
          toast.error('Veuillez renseigner votre date de naissance');
          setLoading(false);
          return;
        }
        await axios.post('/api/auth/forgot-password', {
          email: formData.email,
          date_of_birth: formData.date_of_birth,
          siret: formData.siret || undefined,
        });
        toast.success('Code envoyé par email');
        setResetCode('');
        setMode('reset_code');
        setLoading(false);
      } else if (mode === 'reset_code') {
        if (!/^\d{6}$/.test(resetCode)) {
          toast.error('Code à 6 chiffres attendu');
          setLoading(false);
          return;
        }
        const r = await axios.post('/api/auth/verify-reset-code', {
          email: formData.email,
          code: resetCode,
        });
        setResetToken(r.data?.reset_token);
        setMode('reset');
        setLoading(false);
      } else if (mode === 'reset') {
        await axios.post('/api/auth/reset-password', {
          reset_token: resetToken,
          new_password: formData.password
        });
        toast.success('Mot de passe réinitialisé!');
        setMode('login');
        setFormData({ ...formData, password: '' });
        setLoading(false);
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
      // Reload captcha on registration failure
      if (mode === 'register') loadCaptcha();
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
    
    // Live email verification on register mode
    if (e.target.name === 'email' && mode === 'register') {
      const email = e.target.value.trim();
      setEmailStatus(null);
      setEmailError('');
      
      if (emailCheckTimer.current) clearTimeout(emailCheckTimer.current);
      
      if (email.length > 5 && email.includes('@') && email.includes('.')) {
        setEmailStatus('checking');
        emailCheckTimer.current = setTimeout(async () => {
          try {
            const res = await axios.post('/api/auth/verify-email', { email });
            if (res.data.valid) {
              setEmailStatus('valid');
              setEmailError('');
            } else {
              setEmailStatus('invalid');
              setEmailError(res.data.reason);
            }
          } catch {
            setEmailStatus(null);
          }
        }, 800);
      }
    }
  };

  // Submit OTP code (login or signup verification)
  const submitOtp = async () => {
    if (!otpFlow) return;
    const code = (otpCode || '').trim();
    if (!/^\d{6}$/.test(code)) {
      toast.error('Entrez les 6 chiffres du code');
      return;
    }
    setLoading(true);
    try {
      if (otpFlow.kind === 'login') {
        await loginVerify(otpFlow.userId, code);
        toast.success('Connexion réussie !');
      } else {
        await axios.post('/api/auth/me/email/verify-code', { code });
        // Enter the app directly — no second login step after signup
        await adoptSession(otpFlow.token, otpFlow.user);
        toast.success('Email vérifié ! Bienvenue sur Destine 🎬');
      }
      setOtpFlow(null);
      setOtpCode('');
      setTimeout(() => {
        if (onClose) onClose();
        setTimeout(() => navigate('/films', { replace: true }), 100);
      }, 150);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (!otpFlow) return;
    setOtpResending(true);
    try {
      if (otpFlow.kind === 'login') {
        // Re-run login/start with the same email+password to reissue a code
        const r = await axios.post('/api/auth/login/start', { email: formData.email, password: formData.password });
        if (r.data?.two_factor) {
          setOtpFlow({ kind: 'login', userId: r.data.user_id, emailMasked: r.data.email_masked });
          toast.success('Nouveau code envoyé');
        }
      } else {
        await axios.post('/api/auth/me/email/request-code');
        toast.success('Nouveau code envoyé');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible d\'envoyer un nouveau code');
    } finally {
      setOtpResending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div 
        ref={modalContentRef}
        className={`relative w-full max-w-md bg-gray-900 rounded-3xl shadow-2xl border border-white/10 overflow-y-auto my-4 mx-4 transition-all duration-300 ${
          keyboardVisible 
            ? 'max-h-[60vh] mt-2' 
            : 'max-h-[85vh] mt-[5vh]'
        }`}
      >
        <button
          data-testid="auth-modal-close-btn"
          onClick={onClose}
          className="sticky top-4 left-full ml-auto mr-4 p-2 hover:bg-white/10 rounded-full transition-colors z-10 bg-gray-900/80 backdrop-blur-sm"
        >
          <X className="w-5 h-5" />
        </button>

        {/* --- OTP overlay (2FA login or signup verification) --- */}
        {otpFlow && (
          <div data-testid="otp-overlay" className="p-8 pt-2">
            <div className="text-center mb-6">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/25 to-pink-500/25 border border-purple-400/40 flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-purple-200" />
              </div>
              <h2 className="text-2xl font-bold mb-2">
                {otpFlow.kind === 'signup' ? 'Confirme ton inscription' : 'Sécurité renforcée'}
              </h2>
              <p className="text-sm text-white/70">
                On t&apos;a envoyé un code à 6 chiffres à<br />
                <span className="font-semibold text-white">{otpFlow.emailMasked}</span>.
              </p>
              <p className="text-xs text-white/40 mt-2">
                {otpFlow.kind === 'signup' ? 'Valide 24 heures.' : 'Valide 15 minutes.'}
              </p>
            </div>

            <input
              data-testid="otp-code-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              className="w-full text-center text-3xl font-bold tracking-[0.5em] bg-white/10 border-2 border-white/20 focus:border-purple-500 rounded-xl px-4 py-4 text-white outline-none tabular-nums"
            />

            <button
              data-testid="otp-submit-btn"
              onClick={submitOtp}
              disabled={loading || otpCode.length !== 6}
              className="w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold disabled:opacity-50"
            >
              {loading ? 'Vérification…' : 'Valider'}
            </button>

            <div className="mt-4 text-center space-y-2">
              <button
                type="button"
                data-testid="otp-resend-btn"
                onClick={resendOtp}
                disabled={otpResending}
                className="text-sm text-purple-300 hover:text-purple-200 disabled:opacity-50"
              >
                {otpResending ? 'Envoi…' : 'Renvoyer le code'}
              </button>
              <div>
                <button
                  type="button"
                  data-testid="otp-cancel-btn"
                  onClick={() => { setOtpFlow(null); setOtpCode(''); }}
                  className="text-xs text-white/40 hover:text-white/60"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {!otpFlow && (
        <div className="p-8 pt-2">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">
              {mode === 'login' && t('auth.login')}
              {mode === 'register' && t('auth.register')}
              {mode === 'forgot' && t('auth.forgotPassword')}
              {mode === 'reset' && t('auth.newPassword')}
            </h2>
            <p className="text-gray-400">
              {mode === 'register' && t('auth.joinDestine')}
            </p>
          </div>

          {mode === 'register' && (
            <div className="mb-6">
              <label className="block text-sm font-medium mb-3">{t('auth.accountType')}</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="account-type-id-btn"
                  type="button"
                  onClick={() => setAccountType('ID')}
                  className={`py-3 px-4 rounded-xl font-medium transition-all ${
                    accountType === 'ID'
                      ? 'bg-magenta-500 text-white'
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  {t('auth.accountID')}
                </button>
                <button
                  data-testid="account-type-pro-btn"
                  type="button"
                  onClick={() => setAccountType('PRO')}
                  className={`py-3 px-4 rounded-xl font-medium transition-all ${
                    accountType === 'PRO'
                      ? 'bg-magenta-500 text-white'
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  {t('auth.accountPRO')}
                </button>
              </div>
              {accountType === 'PRO' && (
                <p className="mt-2 text-[11px] text-gray-400 flex items-start gap-1.5" data-testid="pro-account-hint">
                  <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-magenta-400" />
                  <span>{(PRO_HINT[language] || PRO_HINT.en)}</span>
                </p>
              )}
            </div>
          )}

          {/* Language selector for registration */}
          {mode === 'register' && (
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4 text-cyan-400" />
                {t('auth.appLanguage')}
              </label>
              <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto p-1">
                {supportedLanguages.map(lang => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setLanguage(lang)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-all ${
                      language === lang
                        ? 'bg-cyan-500/20 border border-cyan-500/50 text-cyan-300'
                        : 'bg-white/5 hover:bg-white/10 border border-transparent'
                    }`}
                    data-testid={`lang-select-${lang}`}
                  >
                    <span>{getFlag(lang)}</span>
                    <span className="truncate text-xs">{getLanguageName(lang)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2">
                    {t('auth.fullName')}
                  </label>
                  <input
                    data-testid="register-name-input"
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-magenta-500 transition-all"
                    placeholder={t("auth.yourName")}
                  />
                </div>
                
                {accountType === 'PRO' && (
                  <div>
                    <label htmlFor="date_of_birth" className="block text-sm font-medium mb-2">
                      {t('auth.birthDate')} <span className="text-gray-500">({t('auth.optional')})</span>
                    </label>
                    <input
                      type="date"
                      id="date_of_birth"
                      name="date_of_birth"
                      data-testid="register-dob-input"
                      value={formData.date_of_birth}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-magenta-500 transition-all"
                    />
                  </div>
                )}
              </>
            )}
            
            {mode === 'forgot' && (
              <div>
                <label htmlFor="date_of_birth" className="block text-sm font-medium mb-2">
                  {t('auth.birthDate')}
                </label>
                <input
                  type="date"
                  id="date_of_birth"
                  name="date_of_birth"
                  data-testid="auth-forgot-dob-input"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-magenta-500 transition-all"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Pour des raisons de sécurité, veuillez renseigner votre date de naissance
                </p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email
              </label>
              <div className="relative">
                <input
                  data-testid={mode === 'login' ? 'login-email-input' : 'register-email-input'}
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-3 bg-white/5 border rounded-xl focus:outline-none focus:ring-2 transition-all ${
                    mode === 'register' && emailStatus === 'invalid'
                      ? 'border-red-500/50 focus:ring-red-500'
                      : mode === 'register' && emailStatus === 'valid'
                      ? 'border-green-500/50 focus:ring-green-500'
                      : 'border-white/10 focus:ring-magenta-500'
                  }`}
                  placeholder="votre@email.com"
                />
                {mode === 'register' && emailStatus === 'checking' && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {mode === 'register' && emailStatus === 'valid' && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-sm" data-testid="email-valid-icon">
                    &#10003;
                  </div>
                )}
                {mode === 'register' && emailStatus === 'invalid' && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-sm" data-testid="email-invalid-icon">
                    &#10007;
                  </div>
                )}
              </div>
              {mode === 'register' && emailError && (
                <p className="text-xs text-red-400 mt-1.5" data-testid="email-error-msg">{emailError}</p>
              )}
            </div>

            {mode === 'reset_code' && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Code reçu par email
                </label>
                <input
                  data-testid="auth-reset-code-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-magenta-500 text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  required
                />
                <p className="text-xs text-gray-400 mt-2">
                  Un code à 6 chiffres a été envoyé à {formData.email}
                </p>
              </div>
            )}

            {mode !== 'forgot' && mode !== 'reset_code' && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-2">
                  {mode === 'reset' ? t('auth.newPassword') : t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    data-testid={mode === 'login' ? 'login-password-input' : 'register-password-input'}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 pr-12 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-magenta-500 transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            {mode === 'register' && accountType === 'PRO' && (
              <div>
                <label htmlFor="siret" className="block text-sm font-medium mb-2">
                  Numéro SIRET <span className="text-gray-500">(optionnel)</span>
                </label>
                <input
                  data-testid="register-siret-input"
                  type="text"
                  id="siret"
                  name="siret"
                  value={formData.siret}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-magenta-500 transition-all"
                  placeholder="123 456 789 00010"
                />
              </div>
            )}
            
            {mode === 'register' && (
              <div className="flex items-start space-x-3 p-4 bg-white/5 rounded-xl border border-white/10">
                <input
                  type="checkbox"
                  id="accepts_terms"
                  name="accepts_terms"
                  checked={formData.accepts_terms}
                  onChange={(e) => setFormData({ ...formData, accepts_terms: e.target.checked })}
                  required
                  className="mt-1 w-4 h-4 accent-magenta-500"
                />
                <label htmlFor="accepts_terms" className="text-sm text-gray-300 flex-1">
                  J'ai plus de 15 ans et j'accepte les{' '}
                  <a href="https://info.destine.studio" target="_blank" rel="noopener noreferrer" className="text-magenta-400 hover:text-magenta-300 underline">
                    Conditions Générales d'Utilisation
                  </a>
                </label>
              </div>
            )}

            {/* Anti-robot captcha */}
            {(mode === 'register' || mode === 'login') && captcha && (
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <label className="block text-sm font-medium mb-2">
                  Vérification anti-robot
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 px-4 py-2.5 bg-gradient-to-r from-magenta-500/20 to-violet-600/20 border border-white/10 rounded-xl font-mono text-lg font-bold tracking-wider select-none">
                    {captcha.question} = ?
                  </div>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    onFocus={(e) => {
                      setTimeout(() => {
                        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 300);
                    }}
                    placeholder="?"
                    className="w-20 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-magenta-500 text-center text-lg font-mono"
                    data-testid="captcha-answer-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={loadCaptcha}
                    className="p-2 text-gray-400 hover:text-white transition-colors"
                    title="Nouveau calcul"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/></svg>
                  </button>
                </div>
              </div>
            )}

            <button
              data-testid={mode === 'login' ? 'login-submit-btn' : 'register-submit-btn'}
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-gradient-to-r from-magenta-500 to-violet-600 hover:from-magenta-600 hover:to-violet-700 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Chargement...' : (
                <>
                  {mode === 'login' && t('auth.signIn')}
                  {mode === 'register' && 'Créer un compte'}
                  {mode === 'forgot' && 'Envoyer le code par email'}
                  {mode === 'reset_code' && 'Vérifier le code'}
                  {mode === 'reset' && 'Réinitialiser'}
                </>
              )}
            </button>
          </form>

          {mode === 'login' && (
            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  setMode('forgot');
                  setFormData({ ...formData, password: '' });
                }}
                data-testid="auth-forgot-btn"
                className="text-sm text-magenta-400 hover:text-magenta-300 transition-colors"
              >
                {t('auth.forgotPassword')}
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode-btn"
              onClick={() => {
                if (mode === 'forgot' || mode === 'reset' || mode === 'reset_code') {
                  setMode('login');
                } else {
                  setMode(mode === 'login' ? 'register' : 'login');
                }
                setFormData({ email: '', password: '', name: '', siret: '' });
              }}
              className="text-gray-400 hover:text-white transition-colors"
            >
              {(mode === 'login') && t('auth.noAccount')}
              {(mode === 'register') && t('auth.alreadyAccount')}
              {(mode === 'forgot' || mode === 'reset' || mode === 'reset_code') && 'Retour à la connexion'}
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
